import classNames from 'classnames';
import { FC, HTMLAttributes, ReactNode, useRef, useState, CSSProperties, useCallback, useLayoutEffect } from 'react';

import { ButtonVariant } from '@/shared/ui/Button';
import { Button } from '@/shared/ui/Button/Button';

import { ConditionView } from '../ConditionView/ConditionView';

import './Truncatable.scss';

interface Props extends HTMLAttributes<HTMLDivElement> {
  text: ReactNode;
  lines?: number;
  readMoreLabel?: React.ReactNode;
  readLessLabel?: React.ReactNode;
  collapseHeight?: number;
}

export const Truncatable: FC<Props> = ({
  text,
  lines = 2,
  readMoreLabel = 'Читати повністю',
  readLessLabel = 'Згорнути',
  collapseHeight = 60,
  className,
  ...restProps
}) => {
  const textRef = useRef<HTMLDivElement>(null);

  const [shouldTruncate, setShouldTruncate] = useState<boolean>(false);
  const [isTruncated, setIsTruncated] = useState<boolean>(false);

  const measureTextHeight = useCallback(() => {
    if (!textRef.current) return;

    const fullHeight = textRef.current.scrollHeight;
    const shouldBeTruncated = fullHeight > collapseHeight;

    setShouldTruncate(shouldBeTruncated);
    setIsTruncated(shouldBeTruncated);
  }, [collapseHeight]);

  useLayoutEffect(() => {
    window.addEventListener('resize', measureTextHeight);
    measureTextHeight();

    return () => {
      window.removeEventListener('resize', measureTextHeight);
    };
  }, [measureTextHeight]);

  const onToggleTruncation = (): void => {
    setIsTruncated((prev) => !prev);
  };

  const clampStyle: CSSProperties = {
    WebkitLineClamp: shouldTruncate && isTruncated ? String(lines) : 'unset',
  };

  return (
    <div data-testid="truncatable" {...restProps} className={classNames('custom-truncatable-wrapper', className)}>
      <div
        data-testid="truncatable-text"
        ref={textRef}
        className={classNames('truncatable', {
          expanded: !isTruncated && shouldTruncate,
        })}
        style={clampStyle}
      >
        {text}
      </div>

      <ConditionView if={shouldTruncate}>
        <Button data-testid="truncatable-button" variant={ButtonVariant.GHOST} onClick={onToggleTruncation}>
          <ConditionView if={isTruncated} else={readLessLabel}>
            {readMoreLabel}
          </ConditionView>
        </Button>
      </ConditionView>
    </div>
  );
};
